import { LockClosedIcon } from "@heroicons/react/20/solid";
import React, { useEffect, useRef, useState } from "react";
import type { Decision } from "../../api/access/decisions";
import BlockedState from "../feedback/BlockedState";
import DropdownPopover from "../overlays/DropdownPopover";

interface LockedButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Refused for a permission one lacks, with its remedy. */
  decision: Decision;
}

/**
 * An action refused for a permission one lacks (redesign R3-5 `blocked`): it stays in view with
 * a lock, and pressing it opens BlockedState beside it, with Ask where one may. A click outside
 * or Escape closes it.
 */
export default function LockedButton({ decision, children, onClick: _ignored, disabled: _focusable, type: _type, ...props }: LockedButtonProps) {
  const [open, setOpen] = useState(false);
  const anchor = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!open) return undefined;
    const closeOutside = (event: PointerEvent) => {
      if (event.target instanceof Node && anchor.current?.contains(event.target) !== true) setOpen(false);
    };
    document.addEventListener("pointerdown", closeOutside);
    return () => { document.removeEventListener("pointerdown", closeOutside); };
  }, [open]);
  return (
    <span ref={anchor} className="relative inline-flex" onKeyDown={(event) => { if (event.key === "Escape") setOpen(false); }}>
      <button {...props} type="button" aria-disabled="true" aria-expanded={open} onClick={() => { setOpen(!open); }}>
        <LockClosedIcon className="h-3.5 w-3.5" aria-hidden="true" />
        {children}
      </button>
      <DropdownPopover open={open} width="lg">
        <div className="p-3"><BlockedState decision={decision} /></div>
      </DropdownPopover>
    </span>
  );
}
